import { GridColDef } from '@mui/x-data-grid';
import { PlaylistRemove, PlaylistAdd, MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Stack, SvgIcon } from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Row } from '../interfaces';
import useDeletePlan from './useDeletePlan';
import useGenerateXls from '../../Plan/PlanSummary/hooks/useGenerateXls';
import CountryStatus from '../CountryStatus/CountryStatus';
import usePlanStore from '../../../../../store/planStore';

const useMediaPlansTableColumns = (
  setOpenDeleteModal: Dispatch<SetStateAction<boolean>>
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [deletedId, setDeletedId] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { comparedPlans, setComparedPlan, removeComparedPlan } = usePlanStore();

  const { deletePlan } = useDeletePlan();

  const { generateXls } = useGenerateXls();

  const handleAddToComparision = (rowId: string, planName: string) => {
    setComparedPlan(rowId, {
      planName,
    });
  };

  const handleRemoveFromComparision = (rowId: string) => {
    removeComparedPlan(rowId);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = (id: string) => {
    navigate(`/media-planning/edit/${id}`);
    handleMenuClose();
  };

  const handleDelete = (id: string) => {
    setDeletedId(id);
    setOpenDeleteModal(true);
    handleMenuClose();
  };

  const handleDownloadXls = (id: string) => {
    generateXls(id);
  };

  const limitReached = Object.keys(comparedPlans).length >= 3;

  const columns: GridColDef<Row>[] = [
    {
      field: 'planName',
      headerName: 'Plan name',
      flex: 4,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'createdBy',
      headerName: 'Created by',
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'updatedBy',
      headerName: 'Updated by',
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated on',
      flex: 0.8,
      sortable: true,
      disableColumnMenu: true,
      valueFormatter: (params) => format(new Date(params.value), 'dd-MM-yyyy'),
    },
    {
      field: 'metaBooked',
      headerName: 'Meta booked',
      width: 240,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Stack flexDirection="row" width="100%">
            <Stack flexDirection="row" gap={1}>
              {params.row.statuses.map((countryStatus) => (
                <CountryStatus
                  countryStatus={countryStatus}
                  key={countryStatus.country}
                />
              ))}
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {!comparedPlans[params.row.id] ? (
            <IconButton
              onClick={() =>
                handleAddToComparision(params.row.id, params.row.planName)
              }
              disabled={limitReached}
            >
              <SvgIcon
                width="20"
                height="18"
                viewBox="0 0 20 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8164 -4.728e-07L8.68359 -3.79572e-07L8.68359 8.63281L10.8164 8.63281L10.8164 -4.728e-07ZM5.68248e-07 13L2.18359 13L2.18359 -9.54479e-08L0 0L5.68248e-07 13ZM4.31641 13L6.5 13L6.5 -2.84124e-07L4.31641 -1.88676e-07L4.31641 13Z"
                  fill={limitReached ? 'currentColor' : '#525F81'}
                />
                <path
                  d="M19.666 10.8672H15.3496V6.5H13.166V10.8672H8.84961V13H13.166V17.3672H15.3496V13H19.666V10.8672Z"
                  fill={limitReached ? 'currentColor' : '#388C2D'}
                />
              </SvgIcon>
            </IconButton>
          ) : (
            <IconButton
              onClick={() => handleRemoveFromComparision(params.row.id)}
            >
              <SvgIcon
                width="20"
                height="13"
                viewBox="0 0 20 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8164 -4.728e-07L8.68359 -3.79572e-07L8.68359 8.63281L10.8164 8.63281L10.8164 -4.728e-07ZM5.68248e-07 13L2.18359 13L2.18359 -9.54479e-08L0 0L5.68248e-07 13ZM4.31641 13L6.5 13L6.5 -2.84124e-07L4.31641 -1.88676e-07L4.31641 13Z"
                  fill="#525F81"
                />
                <path
                  d="M19.666 13V10.8672H8.84961V13H19.666Z"
                  fill="#F00F00"
                />
              </SvgIcon>
            </IconButton>
          )}
          <IconButton onClick={(event) => handleMenuOpen(event, params.row.id)}>
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow === params.row.id}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleEdit(params.row.id)}>
              {t('mediaPlanning.editPlan')}
            </MenuItem>
            <MenuItem
              onClick={() => handleDelete(params.row.id)}
              disabled={params.row.statuses.some(
                (countryStatus) =>
                  countryStatus.status === 'reserved' ||
                  countryStatus.status === 'partially-reserved'
              )}
            >
              {t('mediaPlanning.deletePlan')}
            </MenuItem>
            <MenuItem onClick={() => handleDownloadXls(params.row.id)}>
              {t('mediaPlanning.downloadXls')}
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return { columns, deletePlan, deletedId };
};

export default useMediaPlansTableColumns;
